/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : ShowLogsDetails.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useState, useMemo } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import CustomPaginationV2 from "../common/pagination/CustomPaginationV2";
import CustomDateRangePicker from "../common/datepicker/CustomDateRangePicker";

dayjs.extend(utc);

function ShowLogsDetails({
  expanded,
  setExpanded,
  logs = [],
  loading,
  logsCount,
  page,
  rowsPerPage,
  onPageChange,
}) {
  const [selectedDate, setSelectedDate] = useState(null);
  const [logsDateRange, setLogsDateRange] = useState([null, null]);

  const handleAccordionToggle = (id) => {
    setExpanded((prev) => (prev === id ? null : id));
  };

  // ✅ Filter logs by selected date
  const filteredLogs = useMemo(() => {
    const [start, end] = logsDateRange;

    if (!start || !end) return logs;

    return logs?.filter((log) => {
      const logDate = dayjs.utc(log?.created_at).local();
      return (
        logDate.isSame(start, "day") ||
        logDate.isSame(end, "day") ||
        (logDate.isAfter(start, "day") && logDate.isBefore(end, "day"))
      );
    });
  }, [logsDateRange, logs]);


  return (
    <Box>
      {/* Date Selector */}
      <Box
        sx={{
          mb: 2,
          width: 250,
          display: "flex",
          justifyContent: "flex-end",
          ml: "auto",
        }}
      >

        {logs?.length > 0 && (
          <CustomDateRangePicker
            logsDateRange={logsDateRange}
            setLogsDateRange={setLogsDateRange}
            filteredLogs={filteredLogs}
          />
        )}
      </Box>

      {/* Logs Section */}
      <Box
        sx={{
          borderRadius: 0,
          minHeight: 100,
          maxHeight: 200,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          overflowY: "auto",
          pr: 1,
          "&::-webkit-scrollbar": { width: "8px" },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#c1c1c1",
            borderRadius: "8px",
          },
        }}
      >
        {loading ? (
          <Box sx={{ textAlign: "center", mt: 5 }}>
            <CircularProgress size={30} />
          </Box>
        ) : filteredLogs.length > 0 ? (
          filteredLogs?.map((log, idx) => (
            <>
              <Accordion
                key={idx}
                expanded={expanded === idx}
                sx={{
                  boxShadow: 3,
                  borderRadius: 1,
                  backgroundColor: "#f9f9f9",
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary
                  onClick={() => handleAccordionToggle(idx)}
                  expandIcon={
                    expanded === idx ? (
                      <ExpandLessIcon sx={{ color: "black" }} />
                    ) : (
                      <ExpandMoreIcon sx={{ color: "black" }} />
                    )
                  }
                  sx={{
                    backgroundColor: expanded === idx ? "#d5d7dbff" : "#fff",
                    minHeight: "48px !important",
                    "& .MuiAccordionSummary-content": {
                      margin: "8px 0",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "start",
                    },
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: "black",
                      mb: 0.5,
                      textAlign: "start",
                    }}
                  >
                    Timestamp
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 200,
                      color: "black",
                      mb: 0.5,
                      textAlign: "start",
                    }}
                  >
                    {dayjs(log?.created_at).format("M/D/YYYY, h:mm:ss A")}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    backgroundColor: "white",
                    maxHeight: 400,
                    overflowY: "auto",
                    p: 2,
                  }}
                >
                  <Box sx={{ display: "flex", mb: 1 }}>
                    <Typography sx={{ fontWeight: "bold", mr: 1 }}>
                      Changed by:
                    </Typography>
                    <Typography>{log?.changed_by?.name}</Typography>
                  </Box>
                  <Box sx={{ display: "flex" }}>
                    <Typography sx={{ fontWeight: "bold", mb: 0.5 }}>
                      Changes:
                    </Typography>

                    <Box
                      component="pre"
                      sx={{
                        backgroundColor: "#f4f4f4",
                        p: 1.5,
                        borderRadius: 1,
                        fontSize: "0.85rem",
                        fontFamily: "monospace",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        overflowX: "auto",
                        maxHeight: 300,
                      }}
                    >
                      {typeof log?.current_changes === "object"
                        ? JSON.stringify(log.current_changes, null, 2)
                        : String(log?.current_changes || "—")}
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </>
          ))
        ) : (
          <Typography
            sx={{
              textAlign: "center",
              mt: 5,
              color: "#888",
              fontFamily: "Manrope, sans-serif",
            }}
          >
            No logs found
          </Typography>
        )}
      </Box>
      {Array.isArray(filteredLogs) && filteredLogs.length > 0 && (
        <Box>
          <CustomPaginationV2
            page={page}
            rowsPerPage={rowsPerPage}
            totalItems={logsCount}
            onPageChange={onPageChange}
          />
        </Box>
      )}
    </Box>
  );
}

export default ShowLogsDetails;
